import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getWebhookLogs } from "@/shared/api/services/adminService";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/shared/components/ui/sheet";
import {
  Search,
  RefreshCw,
  ArrowDownToLine,
  ArrowUpFromLine,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  Eye,
  ChevronLeft,
  ChevronRight,
  Activity,
} from "lucide-react";
import { format } from "date-fns";

const C = {
  bg: "#060C18",
  card: "#0E1C35",
  cardHigh: "#122040",
  cardHover: "#162B4D",
  border: "rgba(255,255,255,0.07)",
  borderHover: "rgba(34,211,238,0.35)",
  cyan: "#22D3EE",
  cyanSoft: "rgba(34,211,238,0.08)",
  blue: "#3B82F6",
  blueSoft: "rgba(59,130,246,0.08)",
  emerald: "#10B981",
  emeraldSoft: "rgba(16,185,129,0.08)",
  amber: "#F59E0B",
  amberSoft: "rgba(245,158,11,0.08)",
  rose: "#FB7185",
  slate: "#64748B",
  slateSoft: "rgba(100,116,139,0.08)",
  t1: "#EFF6FF",
  t2: "#93B4D4",
  t3: "#4D6E90",
  t4: "#2A4060",
};

const DARK_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap');

.webhook-dark, .webhook-dark * {
  font-family: 'Outfit', system-ui, sans-serif !important;
  box-sizing: border-box;
}

/* ── Search fix: icon + input separated properly ── */
.webhook-dark .wh-search-wrap {
  position: relative;
  flex: 1 1 250px;
  min-width: 250px;
  display: flex;
  align-items: center;
}

.webhook-dark .wh-search-icon {
  position: absolute;
  left: 13px;
  top: 50%;
  transform: translateY(-50%);
  color: #4D6E90;
  pointer-events: none;
  z-index: 2;
  display: flex;
  align-items: center;
  transition: color .2s;
  flex-shrink: 0;
}

.webhook-dark .wh-search-input {
  width: 100%;
  height: 42px;
  padding: 0 14px 0 40px !important;
  background: #0E1C35 !important;
  border: 1px solid rgba(255,255,255,0.09) !important;
  color: #EFF6FF !important;
  border-radius: 10px !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  outline: none !important;
  transition: all .2s cubic-bezier(0.4,0,0.2,1) !important;
  line-height: 42px;
}

.webhook-dark .wh-search-input::placeholder {
  color: #4D6E90 !important;
  font-weight: 400 !important;
}

.webhook-dark .wh-search-input:hover {
  border-color: rgba(34,211,238,0.35) !important;
  background: #122040 !important;
}

.webhook-dark .wh-search-wrap:hover .wh-search-icon,
.webhook-dark .wh-search-wrap:focus-within .wh-search-icon {
  color: #22D3EE !important;
}

.webhook-dark .wh-search-input:focus {
  border-color: rgba(34,211,238,0.55) !important;
  box-shadow: 0 0 0 3px rgba(34,211,238,0.12) !important;
  background: #122040 !important;
}

/* Selects */
.webhook-dark [data-radix-select-trigger],
.webhook-dark [role="combobox"] {
  background: #0E1C35 !important;
  border: 1px solid rgba(255,255,255,0.09) !important;
  color: #EFF6FF !important;
  border-radius: 10px !important;
  height: 42px !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  transition: all .2s !important;
}

.webhook-dark [data-radix-select-trigger]:hover,
.webhook-dark [role="combobox"]:hover {
  border-color: rgba(34,211,238,0.35) !important;
  color: #22D3EE !important;
  background: #122040 !important;
}

.webhook-dark [data-radix-select-content] {
  background: #0E1C35 !important;
  border: 1px solid rgba(34,211,238,0.2) !important;
  border-radius: 12px !important;
  box-shadow: 0 8px 32px rgba(0,0,0,0.5) !important;
}

.webhook-dark [data-radix-select-item] {
  color: #93B4D4 !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  border-radius: 8px !important;
}

.webhook-dark [data-radix-select-item]:hover,
.webhook-dark [data-radix-select-item][data-highlighted] {
  background: rgba(34,211,238,0.08) !important;
  color: #22D3EE !important;
}

/* Sheet (detail panel) */
.webhook-dark [data-radix-sheet-content],
.webhook-dark [data-state] > [data-radix-sheet-content] {
  background: #060C18 !important;
  border-left: 1px solid rgba(34,211,238,0.15) !important;
}

@keyframes spin { to { transform: rotate(360deg); } }

::-webkit-scrollbar { width: 6px; height: 6px; }
::-webkit-scrollbar-track { background: rgba(14,28,53,0.3); border-radius: 10px; }
::-webkit-scrollbar-thumb { background: rgba(34,211,238,0.2); border-radius: 10px; }
::-webkit-scrollbar-thumb:hover { background: rgba(34,211,238,0.35); }
`;

const statusConfig = {
  received: { icon: Clock, color: C.blue, bg: C.blueSoft, label: "received" },
  validated: { icon: CheckCircle2, color: C.emerald, bg: C.emeraldSoft, label: "validated" },
  processed: { icon: CheckCircle2, color: C.emerald, bg: C.emeraldSoft, label: "processed" },
  failed: { icon: XCircle, color: C.rose, bg: "rgba(251,113,133,0.08)", label: "failed" },
  invalid_signature: { icon: AlertTriangle, color: C.amber, bg: C.amberSoft, label: "invalid_signature" },
  duplicate: { icon: AlertTriangle, color: C.slate, bg: C.slateSoft, label: "duplicate" },
};

export default function WebhookLogs() {
  const { t } = useLanguage();
  const [search, setSearch] = useState("");
  const [direction, setDirection] = useState("all");
  const [eventFilter, setEventFilter] = useState("all");
  const [selectedLog, setSelectedLog] = useState(null);
  const [page, setPage] = useState(0);
  const pageSize = 20;

  React.useEffect(() => {
    const id = "webhook-dark-css";
    if (document.getElementById(id)) return;
    const el = document.createElement("style");
    el.id = id;
    el.textContent = DARK_CSS;
    document.head.appendChild(el);
    return () => document.getElementById(id)?.remove();
  }, []);

  const { data: rawLogs = [], isLoading, isFetching, isError, error, refetch } = useQuery({
    queryKey: ["webhook-logs"],
    queryFn: () => getWebhookLogs(500),
    refetchInterval: 5000,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: false,
  });

  const logs = React.useMemo(() => (rawLogs || []).map((item) => {
    const httpStatus = item.httpStatus;
    const s = item.errorMessage ? "failed" : httpStatus >= 200 && httpStatus < 300 ? "processed" : "failed";
    return { id: item.id, webhook_id: item.id, transaction_id: item.paymentId, source: item.merchantName || item.merchantId || "", direction: "outbound", event_type: item.event, status: s, response_status: httpStatus, response_time_ms: null, created_date: item.createdAt, url: item.url, headers: "{}", payload: item.requestPayload || "{}", response_body: item.responseBody, error_message: item.errorMessage };
  }), [rawLogs]);

  const stats = React.useMemo(() => {
    const total = logs.length;
    const success = logs.filter(l => l.status === "processed").length;
    const failed = logs.filter(l => l.status === "failed").length;
    const rate = total > 0 ? ((success / total) * 100).toFixed(1) : "0.0";
    return { total, success, failed, rate };
  }, [logs]);

  const filteredLogs = React.useMemo(() => logs.filter((log) => {
    if (search) { const sl = search.toLowerCase(); if (!log.webhook_id?.toLowerCase().includes(sl) && !log.transaction_id?.toLowerCase().includes(sl) && !log.source?.toLowerCase().includes(sl)) return false; }
    if (direction !== "all" && log.direction !== direction) return false;
    if (eventFilter !== "all" && log.event_type !== eventFilter) return false;
    return true;
  }), [logs, search, direction, eventFilter]);

  const paginatedLogs = filteredLogs.slice(page * pageSize, (page + 1) * pageSize);
  const totalPages = Math.ceil(filteredLogs.length / pageSize);

  return (
    <div className="webhook-dark" style={{ minHeight: "100vh", background: C.bg }}>
      <div aria-hidden style={{ position: "fixed", inset: 0, pointerEvents: "none", backgroundImage: "linear-gradient(rgba(34,211,238,0.015) 1px,transparent 1px),linear-gradient(90deg,rgba(34,211,238,0.015) 1px,transparent 1px)", backgroundSize: "60px 60px", maskImage: "radial-gradient(ellipse 80% 80% at 50% 30%,black 20%,transparent 100%)" }} />
      <div aria-hidden style={{ position: "fixed", inset: 0, pointerEvents: "none", background: "radial-gradient(ellipse 1000px 600px at 5% 5%,rgba(34,211,238,0.04) 0%,transparent 70%),radial-gradient(ellipse 800px 500px at 95% 85%,rgba(59,130,246,0.04) 0%,transparent 65%)" }} />

      <div className="max-w-[1440px] mx-auto md:p-8 flex flex-col gap-6 relative z-10">
        {/* Header */}
        <div className="flex flex-col justify-between gap-4 sm:flex-row">
          <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
            <div style={{ width: 60, height: 60, borderRadius: 18, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, background: "linear-gradient(135deg,rgba(34,211,238,0.1) 0%,rgba(59,130,246,0.1) 100%)", border: "1px solid rgba(34,211,238,0.22)", boxShadow: "0 8px 24px rgba(34,211,238,0.1)" }}>
              <Activity size={26} style={{ color: C.cyan }} />
            </div>
            <div>
              <h1 style={{ fontSize: 30, fontWeight: 800, color: C.t1, margin: 0, letterSpacing: "-0.6px" }}>{t("webhookLogs")}</h1>
              <p style={{ fontSize: 13, color: C.t3, margin: "6px 0 0", fontWeight: 500 }}>
                <span style={{ color: C.cyan }}>{filteredLogs.length}</span> {t("webhookEvents")}
                {isFetching && !isLoading && <span style={{ marginLeft: 8, fontSize: 11, color: C.t4 }}>● {t("updating") || "Updating..."}</span>}
              </p>
            </div>
          </div>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "0 20px", height: 44, borderRadius: 11, fontSize: 13, fontWeight: 700, cursor: isFetching ? "not-allowed" : "pointer", border: `1px solid ${C.border}`, background: C.card, color: C.t2, transition: "all .2s", opacity: isFetching ? 0.6 : 1 }}
            onMouseEnter={(e) => { if (!isFetching) { e.currentTarget.style.borderColor = C.borderHover; e.currentTarget.style.color = C.cyan; e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(34,211,238,0.15)"; } }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.t2; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
          >
            <RefreshCw size={15} style={{ animation: isFetching ? "spin 1s linear infinite" : "none" }} />
            {t("refresh")}
          </button>
        </div>

        {/* ── Stats Cards ── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))", gap: 12, marginBottom: 8 }}>
          {[
            { label: t("totalEvents") || "Total Events", value: stats.total, color: C.cyan, bg: C.cyanSoft },
            { label: t("processed"), value: stats.success, color: C.emerald, bg: C.emeraldSoft },
            { label: t("failed"), value: stats.failed, color: C.rose, bg: "rgba(251,113,133,0.08)" },
            { label: t("successRate") || "Success Rate", value: `${stats.rate}%`, color: stats.rate >= 95 ? C.emerald : stats.rate >= 80 ? C.amber : C.rose, bg: stats.rate >= 95 ? C.emeraldSoft : stats.rate >= 80 ? C.amberSoft : "rgba(251,113,133,0.08)" },
          ].map(({ label, value, color, bg }) => (
            <div key={label} style={{ background: `linear-gradient(155deg,${C.cardHigh} 0%,${C.card} 100%)`, border: `1px solid ${C.border}`, borderRadius: 14, padding: "16px 18px", position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 1, background: `linear-gradient(90deg,transparent,${color}30,transparent)` }} />
              <p style={{ fontSize: 11, fontWeight: 700, color: C.t3, textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 6px" }}>{label}</p>
              <p style={{ fontSize: 22, fontWeight: 800, color, margin: 0, letterSpacing: "-0.5px" }}>{value}</p>
            </div>
          ))}
        </div>

        {/* ── Filters (FIXED) ── */}
        <div style={{ background: `linear-gradient(155deg,${C.cardHigh} 0%,${C.card} 100%)`, border: `1px solid ${C.border}`, borderRadius: 16, padding: "18px 20px", boxShadow: "0 4px 24px rgba(0,0,0,0.2)", marginBottom: 24, position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 1, background: "linear-gradient(90deg,transparent,rgba(34,211,238,0.15),transparent)" }} />
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
            {/* Fixed search: icon absolutely positioned, input has left padding */}
            <div className="wh-search-wrap">
              <span className="wh-search-icon">
                <Search size={15} />
              </span>
              <input
                type="text"
                className="wh-search-input"
                placeholder={t("searchByWebhookId")}
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              />
            </div>

            <div style={{ flex: "0 0 auto" }}>
              <Select value={direction} onValueChange={(v) => { setDirection(v); setPage(0); }}>
                <SelectTrigger style={{ width: 150 }}>
                  <SelectValue placeholder={t("direction")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("allDirection")}</SelectItem>
                  <SelectItem value="inbound">{t("inbound")}</SelectItem>
                  <SelectItem value="outbound">{t("outbound")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div style={{ flex: "0 0 auto" }}>
              <Select value={eventFilter} onValueChange={(v) => { setEventFilter(v); setPage(0); }}>
                <SelectTrigger style={{ width: 200 }}>
                  <SelectValue placeholder={t("event") || "Event"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("All Events") || "All"}</SelectItem>
                  <SelectItem value="payment.succeeded">Payment Succeeded</SelectItem>
                  <SelectItem value="payment.failed">Payment Failed</SelectItem>
                  <SelectItem value="withdrawal.succeeded">Withdrawal Succeeded</SelectItem>
                  <SelectItem value="withdrawal.failed">Withdrawal Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>


          </div>
        </div>

        {/* Table */}
        <div style={{ background: `linear-gradient(155deg,${C.cardHigh} 0%,${C.card} 100%)`, border: `1px solid ${C.border}`, borderRadius: 18, overflow: "hidden", boxShadow: "0 4px 24px rgba(0,0,0,0.2)", position: "relative" }}>
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 1, background: "linear-gradient(90deg,transparent,rgba(34,211,238,0.15),transparent)" }} />
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                  {[t("webhookId"), t("direction"), t("source"), t("event"), t("status"), t("responseStatus"), t("date"), ""].map((h, i) => (
                    <th key={i} style={{ padding: "18px 20px", textAlign: i === 7 ? "center" : "left", fontSize: 11, fontWeight: 700, color: C.t3, textTransform: "uppercase", letterSpacing: "0.1em", background: "rgba(255,255,255,0.02)", width: i === 7 ? 60 : "auto" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr><td colSpan={8} style={{ textAlign: "center", padding: 60, fontSize: 14, color: C.t3, fontWeight: 500 }}>{t("loading")}</td></tr>
                ) : isError ? (
                  <tr><td colSpan={8} style={{ textAlign: "center", padding: 60, fontSize: 14, color: C.rose, fontWeight: 500 }}>{t("failedToLoadWebhookLogs")}{error?.message ? `: ${error.message}` : ""}</td></tr>
                ) : paginatedLogs.length === 0 ? (
                  <tr><td colSpan={8} style={{ textAlign: "center", padding: 60, fontSize: 14, color: C.t3, fontWeight: 500 }}>{t("noWebhookLogs")}</td></tr>
                ) : paginatedLogs.map((log) => {
                  const statusConf = statusConfig[log.status] || statusConfig.received;
                  const StatusIcon = statusConf.icon;
                  return (
                    <tr key={log.id} style={{ borderBottom: `1px solid ${C.border}`, transition: "background .2s" }}
                      onMouseEnter={(e) => e.currentTarget.style.background = "rgba(34,211,238,0.03)"}
                      onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                    >
                      <td style={{ padding: "20px" }}><span style={{ fontSize: 12, fontWeight: 600, color: C.t2, fontFamily: "monospace" }}>{log.webhook_id}</span></td>
                      <td style={{ padding: "20px" }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: 7, fontSize: 11, fontWeight: 700, background: log.direction === "inbound" ? C.blueSoft : C.emeraldSoft, border: `1px solid ${log.direction === "inbound" ? "rgba(59,130,246,0.2)" : "rgba(16,185,129,0.2)"}`, color: log.direction === "inbound" ? C.blue : C.emerald }}>
                          {log.direction === "inbound" ? <ArrowDownToLine size={12} /> : <ArrowUpFromLine size={12} />}
                          {t(log.direction)}
                        </span>
                      </td>
                      <td style={{ padding: "20px" }}><span style={{ fontSize: 13, fontWeight: 600, color: C.t1 }}>{log.source}</span></td>
                      <td style={{ padding: "20px" }}>
                        <span style={{ display: "inline-flex", padding: "4px 10px", borderRadius: 7, fontSize: 11, fontWeight: 600, background: "rgba(255,255,255,0.04)", border: `1px solid ${C.border}`, color: C.t2 }}>{log.event_type}</span>
                      </td>
                      <td style={{ padding: "20px" }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 12px", borderRadius: 8, fontSize: 11, fontWeight: 700, background: statusConf.bg, border: `1px solid ${statusConf.color}30`, color: statusConf.color }}>
                          <StatusIcon size={12} />
                          {t(log.status === "invalid_signature" ? "invalidSignature" : log.status)}
                        </span>
                      </td>
                      <td style={{ padding: "20px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: log.response_status >= 200 && log.response_status < 300 ? C.emerald : log.response_status >= 400 ? C.rose : C.t2 }}>{log.response_status || "-"}</span>
                          {log.response_time_ms && <span style={{ fontSize: 11, color: C.t4 }}>{log.response_time_ms}ms</span>}
                        </div>
                      </td>
                      <td style={{ padding: "20px" }}>
                        <div style={{ fontSize: 12 }}>
                          <div style={{ color: C.t1, fontWeight: 600 }}>{log.created_date ? format(new Date(log.created_date), "MMM d, yyyy") : t("nA")}</div>
                          <div style={{ color: C.t3, marginTop: 2 }}>{log.created_date ? format(new Date(log.created_date), "HH:mm:ss") : ""}</div>
                        </div>
                      </td>
                      <td style={{ padding: "20px", textAlign: "center" }}>
                        <button onClick={() => setSelectedLog(log)} style={{ width: 32, height: 32, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: 8, cursor: "pointer", background: C.card, border: `1px solid ${C.border}`, color: C.t2, transition: "all .2s" }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = C.cardHover; e.currentTarget.style.borderColor = C.borderHover; e.currentTarget.style.color = C.cyan; e.currentTarget.style.boxShadow = "0 0 8px rgba(34,211,238,0.15)"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = C.card; e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.t2; e.currentTarget.style.boxShadow = "none"; }}
                        >
                          <Eye size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 24 }}>
            <p style={{ fontSize: 13, fontWeight: 600, color: C.t3, margin: 0 }}>
              {t("showing")} <span style={{ color: C.t1 }}>{page * pageSize + 1}</span> {t("to")} <span style={{ color: C.t1 }}>{Math.min((page + 1) * pageSize, filteredLogs.length)}</span> {t("of")} <span style={{ color: C.cyan }}>{filteredLogs.length}</span>
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {[{ dir: -1, icon: ChevronLeft, disabled: page === 0 }, { dir: 1, icon: ChevronRight, disabled: page >= totalPages - 1 }].map(({ dir, icon: Icon, disabled }) => (
                <button key={dir} onClick={() => setPage(page + dir)} disabled={disabled} style={{ width: 38, height: 38, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 10, cursor: disabled ? "not-allowed" : "pointer", background: C.card, border: `1px solid ${C.border}`, color: C.t2, opacity: disabled ? 0.4 : 1, transition: "all .2s" }}
                  onMouseEnter={(e) => { if (!disabled) { e.currentTarget.style.borderColor = C.borderHover; e.currentTarget.style.color = C.cyan; e.currentTarget.style.boxShadow = "0 0 8px rgba(34,211,238,0.15)"; } }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.t2; e.currentTarget.style.boxShadow = "none"; }}
                >
                  <Icon size={16} />
                </button>
              ))}
              <span style={{ fontSize: 13, color: C.t2, fontWeight: 600, padding: "0 4px" }}>{t("page")} {page + 1} {t("of")} {totalPages}</span>
            </div>
          </div>
        )}

        {/* Detail Sheet */}
        <Sheet open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
          <SheetContent className="w-full sm:max-w-xl overflow-y-auto webhook-dark" style={{ background: C.bg, borderLeft: "1px solid rgba(34,211,238,0.15)" }}>
            <SheetHeader>
              <SheetTitle style={{ color: C.t1, fontSize: 18, fontWeight: 700 }}>{t("webhookDetails")}</SheetTitle>
            </SheetHeader>
            {selectedLog && (
              <div style={{ marginTop: 24, display: "flex", flexDirection: "column", gap: 20 }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 16 }}>
                  {[
                    { label: t("webhookId"), value: selectedLog.webhook_id, mono: true },
                    { label: t("transactionId"), value: selectedLog.transaction_id || t("nA"), mono: true },
                  ].map(({ label, value, mono }) => (
                    <div key={label}>
                      <p style={{ fontSize: 11, color: C.t3, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", margin: "0 0 6px" }}>{label}</p>
                      <p style={{ fontSize: 12, color: C.t1, fontWeight: 600, fontFamily: mono ? "monospace" : "inherit", margin: 0 }}>{value}</p>
                    </div>
                  ))}
                  <div>
                    <p style={{ fontSize: 11, color: C.t3, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", margin: "0 0 6px" }}>{t("direction")}</p>
                    <span style={{ display: "inline-flex", padding: "4px 10px", borderRadius: 7, fontSize: 11, fontWeight: 700, background: "rgba(255,255,255,0.04)", border: `1px solid ${C.border}`, color: C.t2 }}>{t(selectedLog.direction)}</span>
                  </div>
                  <div>
                    <p style={{ fontSize: 11, color: C.t3, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", margin: "0 0 6px" }}>{t("status")}</p>
                    <span style={{ display: "inline-flex", padding: "4px 10px", borderRadius: 7, fontSize: 11, fontWeight: 700, background: statusConfig[selectedLog.status]?.bg, border: `1px solid ${statusConfig[selectedLog.status]?.color}30`, color: statusConfig[selectedLog.status]?.color }}>
                      {t(selectedLog.status === "invalid_signature" ? "invalidSignature" : selectedLog.status)}
                    </span>
                  </div>
                </div>

                <div>
                  <p style={{ fontSize: 11, color: C.t3, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", margin: "0 0 8px" }}>{t("url")}</p>
                  <p style={{ fontSize: 12, color: C.t1, fontFamily: "monospace", background: C.card, border: `1px solid ${C.border}`, padding: 12, borderRadius: 10, wordBreak: "break-all", margin: 0 }}>{selectedLog.url || t("nA")}</p>
                </div>

                {[
                  { label: t("requestHeaders"), val: selectedLog.headers },
                  { label: t("requestPayload"), val: selectedLog.payload },
                ].map(({ label, val }) => (
                  <div key={label}>
                    <p style={{ fontSize: 11, color: C.t3, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", margin: "0 0 8px" }}>{label}</p>
                    <pre style={{ fontSize: 11, color: "#E5E7EB", background: "#0D1929", padding: 14, borderRadius: 10, overflowX: "auto", maxHeight: 220, margin: 0, border: `1px solid ${C.border}` }}>
                      {val ? JSON.stringify(JSON.parse(val), null, 2) : "{}"}
                    </pre>
                  </div>
                ))}

                <div>
                  <p style={{ fontSize: 11, color: C.t3, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", margin: "0 0 8px" }}>{t("responseStatus")}</p>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                    <span style={{ display: "inline-flex", padding: "4px 10px", borderRadius: 7, fontSize: 11, fontWeight: 700, background: "rgba(255,255,255,0.04)", border: `1px solid ${C.border}`, color: C.t2 }}>{t("status")}: {selectedLog.response_status || t("nA")}</span>
                    {selectedLog.response_time_ms && <span style={{ display: "inline-flex", padding: "4px 10px", borderRadius: 7, fontSize: 11, fontWeight: 700, background: "rgba(255,255,255,0.04)", border: `1px solid ${C.border}`, color: C.t2 }}>{selectedLog.response_time_ms}ms</span>}
                  </div>
                  <pre style={{ fontSize: 11, color: "#E5E7EB", background: "#0D1929", padding: 14, borderRadius: 10, overflowX: "auto", maxHeight: 220, margin: 0, border: `1px solid ${C.border}` }}>{selectedLog.response_body || t("noResponseBody")}</pre>
                </div>

                {selectedLog.error_message && (
                  <div style={{ padding: 14, background: "rgba(251,113,133,0.08)", border: "1px solid rgba(251,113,133,0.22)", borderRadius: 12 }}>
                    <p style={{ fontSize: 12, fontWeight: 700, color: C.rose, margin: "0 0 6px" }}>{t("error")}</p>
                    <p style={{ fontSize: 12, color: C.rose, margin: 0, opacity: 0.85 }}>{selectedLog.error_message}</p>
                  </div>
                )}
              </div>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </div >
  );
}